import { createContext, useContext, useState, useCallback } from 'react';
import { Portal } from './portal';
import { Toast } from './toast';
import { Z } from '../../lib/z-index';

const ToastContext = createContext(null);
let nextId = 1;

export function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([]);

  const dismiss = useCallback((id) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const show = useCallback((toast) => {
    const id = nextId++;
    setToasts((prev) => [...prev, { id, ...toast }]);
    const duration = toast.duration ?? (toast.variant === 'error' ? 5000 : 3000);
    setTimeout(() => dismiss(id), duration);
    return id;
  }, [dismiss]);

  const api = {
    show,
    success: (title, description) => show({ variant: 'success', title, description }),
    error:   (title, description) => show({ variant: 'error',   title, description }),
    info:    (title, description) => show({ variant: 'info',    title, description }),
    warning: (title, description) => show({ variant: 'warning', title, description }),
    dismiss,
  };

  return (
    <ToastContext.Provider value={api}>
      {children}
      <Portal>
        <div
          className="fixed top-0 left-0 right-0 flex flex-col items-center gap-2 pointer-events-none"
          style={{ zIndex: Z.toast, paddingTop: 'max(env(safe-area-inset-top, 0px), 12px)' }}
        >
          {toasts.map((t) => (
            <div key={t.id} data-testid="toast" className="pointer-events-auto px-4" onClick={() => dismiss(t.id)}>
              <Toast {...t} />
            </div>
          ))}
        </div>
      </Portal>
    </ToastContext.Provider>
  );
}

export function useToast() {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be used within ToastProvider');
  return ctx;
}
